import React, { useState, useEffect, useCallback } from 'react';
import './AdminFeedbackManagement.css';
import SvgIcon from '../../components/SvgIcon';

const AdminFeedbackManagement = ({ user }) => {
  // Authorization check
  const isAuthorized = user && (
    (user.role && (user.role.toLowerCase().includes('admin') || user.role.toLowerCase().includes('editor')))
  );

  const [summary, setSummary] = useState({
    totalSubmissions: 0,
    distinctQuestionnairesCount: 0,
    averageRating: 0,
    ratingCount: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    perQuestionStats: [],
    recentSubmissions: []
  });

  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterQuestionnaire, setFilterQuestionnaire] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Selected Submission Detail Modal
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Available questionnaires for filter
  const [questionnaireOptions, setQuestionnaireOptions] = useState([]);

  // Fetch questionnaire definitions for filter dropdown
  useEffect(() => {
    fetch('/api/feedback/definitions')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success' && Array.isArray(data.data)) {
          setQuestionnaireOptions(data.data);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch summary statistics
  const fetchSummary = useCallback(async () => {
    if (!isAuthorized) return;
    try {
      const params = new URLSearchParams();
      if (filterQuestionnaire !== 'all') params.append('questionnaireId', filterQuestionnaire);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);

      const res = await fetch(`/api/admin/feedback/summary?${params.toString()}`, {
        headers: { 'x-user-role': user?.role || 'Admin' }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSummary(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch feedback summary:', err);
    }
  }, [isAuthorized, filterQuestionnaire, filterRole, filterStartDate, filterEndDate, user]);

  // Fetch filtered submissions list
  const fetchSubmissions = useCallback(async () => {
    if (!isAuthorized) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterQuestionnaire !== 'all') params.append('questionnaireId', filterQuestionnaire);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterSearch.trim()) params.append('search', filterSearch.trim());
      params.append('page', page);
      params.append('limit', limit);

      const res = await fetch(`/api/admin/feedback/submissions?${params.toString()}`, {
        headers: { 'x-user-role': user?.role || 'Admin' }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSubmissions(data.data);
        setTotal(data.total);
        setTotalPages(data.totalPages || 1);
      } else {
        setError(data.message || 'Failed to load feedback submissions.');
      }
    } catch (err) {
      console.error('Failed to fetch feedback submissions:', err);
      setError('Network error while connecting to feedback API.');
    } finally {
      setLoading(false);
    }
  }, [isAuthorized, filterQuestionnaire, filterRole, filterStartDate, filterEndDate, filterSearch, page, limit, user]);

  useEffect(() => {
    fetchSummary();
    fetchSubmissions();
  }, [fetchSummary, fetchSubmissions]);

  const handleResetFilters = () => {
    setFilterQuestionnaire('all');
    setFilterRole('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterSearch('');
    setPage(1);
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterQuestionnaire !== 'all') params.append('questionnaireId', filterQuestionnaire);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterSearch.trim()) params.append('search', filterSearch.trim());

      const res = await fetch(`/api/admin/feedback/export?${params.toString()}`, {
        headers: { 'x-user-role': user?.role || 'Admin' }
      });

      if (!res.ok) {
        throw new Error('Export failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lms_feedback_export_${new Date().toISOString().substring(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('CSV export failed:', err);
      alert('Failed to export feedback CSV. Please check console.');
    } finally {
      setExporting(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="afm-container">
        <div className="afm-header-card">
          <div className="afm-empty-state">
            <div className="afm-empty-icon">
              <SvgIcon name="shield" size={32} />
            </div>
            <h3>Access Restricted</h3>
            <p>You must be an authorized administrator to access the Feedback Management Dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate percentage for rating distribution bars
  const totalRatings = summary.ratingCount || 1;

  return (
    <div className="afm-container">
      {/* Top Header Card */}
      <div className="afm-header-card">
        <div className="afm-header-info">
          <h1 className="afm-title">Questionnaire & Feedback Management</h1>
          <p className="afm-subtitle">
            Review native LMS usability surveys, calculate live ratings, and export responses
          </p>
        </div>
        <div className="afm-header-actions">
          <button 
            type="button" 
            className="afm-btn-refresh"
            onClick={() => { fetchSummary(); fetchSubmissions(); }}
            title="Refresh statistics and responses"
          >
            <SvgIcon name="refresh" size={16} />
            <span>Refresh</span>
          </button>
          <button 
            type="button" 
            className="afm-btn-export"
            onClick={handleExportCsv}
            disabled={exporting || total === 0}
            title="Download CSV export with active filters"
          >
            <SvgIcon name="download" size={16} />
            <span>{exporting ? 'Exporting...' : 'Export CSV'}</span>
          </button>
        </div>
      </div>

      {/* Summary Statistics KPI Cards */}
      <div className="afm-stats-grid">
        <div className="afm-stat-card">
          <div className="afm-stat-icon indigo">
            <SvgIcon name="fileText" size={24} />
          </div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{summary.totalSubmissions}</span>
            <span className="afm-stat-label">Total Submissions</span>
          </div>
        </div>

        <div className="afm-stat-card">
          <div className="afm-stat-icon amber">
            <SvgIcon name="star" size={24} />
          </div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">
              {summary.averageRating > 0 ? `${summary.averageRating} / 5` : 'N/A'}
            </span>
            <span className="afm-stat-label">Average Ease Rating</span>
          </div>
        </div>

        <div className="afm-stat-card">
          <div className="afm-stat-icon green">
            <SvgIcon name="clipboard" size={24} />
          </div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{summary.distinctQuestionnairesCount}</span>
            <span className="afm-stat-label">Active Questionnaires</span>
          </div>
        </div>

        <div className="afm-stat-card">
          <div className="afm-stat-icon blue">
            <SvgIcon name="checkCircle" size={24} />
          </div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{summary.ratingCount}</span>
            <span className="afm-stat-label">Rated Questions Answered</span>
          </div>
        </div>
      </div>

      {/* Rating Scale Breakdown */}
      <div className="afm-rating-analysis-card">
        <h2 className="afm-section-title">
          <SvgIcon name="barChart" size={18} />
          <span>Ease-of-Use Rating Distribution (1 to 5 Stars)</span>
        </h2>
        <div className="afm-rating-breakdown-grid">
          {[5, 4, 3, 2, 1].map(stars => {
            const count = summary.ratingDistribution[stars] || 0;
            const pct = Math.round((count / totalRatings) * 100);
            const labels = {
              5: '5 ★ Very Easy',
              4: '4 ★ Easy',
              3: '3 ★ Neutral',
              2: '2 ★ Difficult',
              1: '1 ★ Very Difficult'
            };
            return (
              <div key={stars} className="afm-rating-bar-box">
                <div className="afm-rating-bar-header">
                  <span>{labels[stars]}</span>
                  <strong>{count} ({pct}%)</strong>
                </div>
                <div className="afm-rating-bar-track">
                  <div 
                    className="afm-rating-bar-fill" 
                    style={{ width: `${pct}%`, backgroundColor: stars >= 4 ? '#10b981' : stars === 3 ? '#f59e0b' : '#ef4444' }} 
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="afm-filter-card">
        <div className="afm-filter-row">
          <div className="afm-filter-group">
            <label className="afm-filter-label" htmlFor="filter-q">Questionnaire / Function</label>
            <select
              id="filter-q"
              className="afm-filter-select"
              value={filterQuestionnaire}
              onChange={(e) => { setFilterQuestionnaire(e.target.value); setPage(1); }}
            >
              <option value="all">All Questionnaires</option>
              {questionnaireOptions.map(q => (
                <option key={q.id} value={q.id}>
                  {q.title} ({q.category})
                </option>
              ))}
            </select>
          </div>

          <div className="afm-filter-group">
            <label className="afm-filter-label" htmlFor="filter-role">User Role</label>
            <select
              id="filter-role"
              className="afm-filter-select"
              value={filterRole}
              onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}
            >
              <option value="all">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Trainer">Trainer</option>
              <option value="Learner">Learner</option>
              <option value="Anonymous">Anonymous</option>
            </select>
          </div>

          <div className="afm-filter-group">
            <label className="afm-filter-label" htmlFor="filter-start">Start Date</label>
            <input
              id="filter-start"
              type="date"
              className="afm-filter-input"
              value={filterStartDate}
              onChange={(e) => { setFilterStartDate(e.target.value); setPage(1); }}
            />
          </div>

          <div className="afm-filter-group">
            <label className="afm-filter-label" htmlFor="filter-end">End Date</label>
            <input
              id="filter-end"
              type="date"
              className="afm-filter-input"
              value={filterEndDate}
              onChange={(e) => { setFilterEndDate(e.target.value); setPage(1); }}
            />
          </div>

          <div className="afm-filter-group" style={{ flex: 1.5 }}>
            <label className="afm-filter-label" htmlFor="filter-search">Search Keyword</label>
            <input
              id="filter-search"
              type="text"
              placeholder="Search feedback text, answers, or usernames..."
              className="afm-filter-input"
              value={filterSearch}
              onChange={(e) => { setFilterSearch(e.target.value); setPage(1); }}
            />
          </div>

          <button 
            type="button" 
            className="afm-filter-btn-reset" 
            onClick={handleResetFilters}
            title="Reset all filters"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Submissions Table Card */}
      <div className="afm-table-card">
        {loading ? (
          <div className="afm-empty-state">
            <SvgIcon name="refresh" size={28} />
            <p>Loading questionnaire submissions...</p>
          </div>
        ) : error ? (
          <div className="afm-empty-state" style={{ color: '#ef4444' }}>
            <SvgIcon name="alertCircle" size={28} />
            <p>{error}</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="afm-empty-state">
            <div className="afm-empty-icon">
              <SvgIcon name="clipboard" size={28} />
            </div>
            <h3>No Feedback Submissions Found</h3>
            <p>No questionnaire responses match the selected filters. Submissions from users will appear here in real-time.</p>
          </div>
        ) : (
          <>
            <div className="afm-table-responsive">
              <table className="afm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Questionnaire</th>
                    <th>LMS Function</th>
                    <th>Role</th>
                    <th>User</th>
                    <th>Submission Time</th>
                    <th>Answers Count</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(sub => {
                    const roleClass = (sub.actorRole || 'anonymous').toLowerCase();
                    return (
                      <tr key={sub.submissionId}>
                        <td><strong>#{sub.submissionId}</strong></td>
                        <td>
                          <strong>{sub.questionnaireTitle || sub.questionnaireId}</strong>
                        </td>
                        <td>{sub.lmsFunction}</td>
                        <td>
                          <span className={`afm-badge-role ${roleClass}`}>
                            {sub.actorRole || 'Anonymous'}
                          </span>
                        </td>
                        <td>
                          {sub.actorUsername ? sub.actorUsername : <span style={{ color: '#94a3b8' }}>Anonymous</span>}
                        </td>
                        <td>{sub.submittedAt}</td>
                        <td>{sub.answers ? sub.answers.length : 0} items</td>
                        <td>
                          <button
                            type="button"
                            className="afm-btn-view-details"
                            onClick={() => setSelectedSubmission(sub)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="afm-pagination-bar">
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                Showing {Math.min((page - 1) * limit + 1, total)} - {Math.min(page * limit, total)} of {total} responses
              </span>
              <div className="afm-pagination-btns">
                <button
                  type="button"
                  className="afm-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, padding: '0 8px' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="afm-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Individual Response Detail Modal */}
      {selectedSubmission && (
        <div 
          className="afm-modal-overlay" 
          onClick={() => setSelectedSubmission(null)}
          role="dialog"
          aria-modal="true"
        >
          <div 
            className="afm-modal-card" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="afm-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#ffffff' }}>
                  Submission #{selectedSubmission.submissionId} Details
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  {selectedSubmission.questionnaireTitle}
                </span>
              </div>
              <button 
                type="button" 
                className="lms-feedback-close-btn"
                onClick={() => setSelectedSubmission(null)}
              >
                &times;
              </button>
            </div>

            <div className="afm-modal-body">
              {/* Metadata */}
              <div className="afm-detail-meta-grid">
                <div className="afm-detail-meta-item">
                  <span className="afm-detail-meta-label">LMS Function:</span>
                  <span className="afm-detail-meta-val">{selectedSubmission.lmsFunction}</span>
                </div>
                <div className="afm-detail-meta-item">
                  <span className="afm-detail-meta-label">Submitted At:</span>
                  <span className="afm-detail-meta-val">{selectedSubmission.submittedAt}</span>
                </div>
                <div className="afm-detail-meta-item">
                  <span className="afm-detail-meta-label">Actor Role:</span>
                  <span className="afm-detail-meta-val">{selectedSubmission.actorRole}</span>
                </div>
                <div className="afm-detail-meta-item">
                  <span className="afm-detail-meta-label">Actor User:</span>
                  <span className="afm-detail-meta-val">{selectedSubmission.actorUsername || 'Anonymous'}</span>
                </div>
              </div>

              {/* Answers */}
              <div className="afm-detail-q-list">
                <h4 style={{ margin: '8px 0 4px', fontSize: '0.95rem', color: '#0f172a' }}>
                  Recorded Responses:
                </h4>
                {selectedSubmission.answers && selectedSubmission.answers.length > 0 ? (
                  selectedSubmission.answers.map((ans, idx) => (
                    <div key={ans.questionId || idx} className="afm-detail-q-item">
                      <p className="afm-detail-q-text">
                        {idx + 1}. {ans.questionText || ans.questionId}
                      </p>
                      <div className="afm-detail-q-ans">
                        {ans.isNa ? (
                          <span style={{ color: '#64748b', fontStyle: 'italic' }}>Not applicable</span>
                        ) : ans.questionType === 'rating' ? (
                          <span style={{ color: '#4f46e5', fontWeight: 800 }}>
                            {ans.answerValue} / 5 Stars
                          </span>
                        ) : ans.answerValue && ans.answerValue.trim() !== '' ? (
                          ans.answerValue
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(No response provided)</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#64748b' }}>No answer records attached.</p>
                )}
              </div>
            </div>

            <div className="afm-modal-footer">
              <button 
                type="button" 
                className="afm-btn-export"
                onClick={() => setSelectedSubmission(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedbackManagement;
